/**
 * Dynamic airline registry that auto-populates from flight API data.
 * Seeded with a comprehensive list of airlines operating to/from/within Africa
 * and major international codeshare partners. Runtime data from /flights
 * responses merges on top.
 */

// Runtime registry — keyed by IATA code for fast lookup
const registry = new Map();

// Comprehensive seed list matching backend seed-airlines.js
const SEED_AIRLINES = [
  // West African carriers
  { iata: "AW", icao: "AFW", name: "Africa World Airlines" },
  { iata: "VJ", icao: "VJN", name: "ValueJet" },
  { iata: "W3", icao: "ARA", name: "Arik Air" },
  { iata: "Q9", icao: "GNJ", name: "Green Africa Airways" },
  { iata: "P4", icao: "APK", name: "Air Peace" },
  { iata: "OF", icao: "OKJ", name: "Overland Airways" },
  { iata: "9J", icao: "DAN", name: "Dana Air" },
  { iata: "VM", icao: "MAX", name: "Max Air" },
  { iata: "ZQ", icao: "AZM", name: "Azman Air" },
  { iata: "5N", icao: "UBD", name: "United Nigeria Airlines" },
  { iata: "QI", icao: "IBS", name: "Ibom Air" },
  { iata: "AJ", icao: "ELK", name: "Aero Contractors" },
  { iata: "4W", icao: "AJK", name: "Allied Jet" },
  { iata: "GO", icao: "GHN", name: "Air Ghana" },
  { iata: "6N", icao: "NIG", name: "Niger Airlines" },
  { iata: "KP", icao: "DSR", name: "ASKY Airlines" },
  { iata: "HF", icao: "VRE", name: "Air Cote d'Ivoire" },
  { iata: "2J", icao: "VUN", name: "Air Burkina" },
  { iata: "HC", icao: "SZN", name: "Air Senegal" },
  { iata: "TQ", icao: "TCR", name: "Transair" },
  { iata: "GI", icao: "GUY", name: "Air Guinee Express" },
  { iata: "L6", icao: "MAI", name: "Mauritania Airlines" },
  { iata: "ML", icao: "FML", name: "Sky Mali" },
  { iata: "QC", icao: "CWK", name: "Camair-Co" },
  { iata: "C8", icao: "CRN", name: "Cronos Airlines" },
  // East African carriers
  { iata: "ET", icao: "ETH", name: "Ethiopian Airlines" },
  { iata: "KQ", icao: "KQA", name: "Kenya Airways" },
  { iata: "WB", icao: "RWD", name: "RwandAir" },
  { iata: "UR", icao: "UGX", name: "Uganda Airlines" },
  { iata: "TC", icao: "TZA", name: "Air Tanzania" },
  { iata: "JM", icao: "JMA", name: "Jambojet" },
  { iata: "5H", icao: "FLY", name: "Fly540" },
  // Southern African carriers
  { iata: "SA", icao: "SAA", name: "South African Airways" },
  { iata: "MN", icao: "CAW", name: "Comair" },
  { iata: "FA", icao: "SFB", name: "FlySafair" },
  { iata: "GE", icao: "LFT", name: "Lift Airlines" },
  { iata: "4Z", icao: "AIZ", name: "Airlink" },
  { iata: "TM", icao: "LAM", name: "LAM Mozambique Airlines" },
  { iata: "DT", icao: "DTA", name: "TAAG Angola Airlines" },
  { iata: "MK", icao: "MAU", name: "Air Mauritius" },
  { iata: "MD", icao: "MDG", name: "Air Madagascar" },
  { iata: "UM", icao: "AUI", name: "Air Zimbabwe" },
  { iata: "P0", icao: "PRF", name: "Proflight Zambia" },
  { iata: "BP", icao: "BOT", name: "Air Botswana" },
  { iata: "SW", icao: "NMB", name: "Air Namibia" },
  // North African carriers
  { iata: "AT", icao: "RAM", name: "Royal Air Maroc" },
  { iata: "3O", icao: "MAC", name: "Air Arabia Maroc" },
  { iata: "O6", icao: "RME", name: "Royal Air Maroc Express" },
  { iata: "MS", icao: "MSR", name: "EgyptAir" },
  { iata: "HZ", icao: "MSE", name: "EgyptAir Express" },
  { iata: "SM", icao: "MSC", name: "Air Cairo" },
  { iata: "TU", icao: "TUN", name: "Tunisair" },
  { iata: "UG", icao: "TUX", name: "Tunisair Express" },
  { iata: "AH", icao: "DAH", name: "Air Algerie" },
  { iata: "SF", icao: "TAR", name: "Tassili Airlines" },
  { iata: "LN", icao: "LBY", name: "Libyan Airlines" },
  { iata: "8U", icao: "LAA", name: "Afriqiyah Airways" },
  { iata: "BJ", icao: "NVR", name: "Nouvelair" },
  // Central African carriers
  { iata: "C2", icao: "EAL", name: "CEIBA Intercontinental" },
  // Middle Eastern carriers
  { iata: "EK", icao: "UAE", name: "Emirates" },
  { iata: "QR", icao: "QTR", name: "Qatar Airways" },
  { iata: "EY", icao: "ETD", name: "Etihad Airways" },
  { iata: "SV", icao: "SVA", name: "Saudia" },
  { iata: "XY", icao: "KNE", name: "Flynas" },
  { iata: "KU", icao: "KAC", name: "Kuwait Airways" },
  { iata: "FZ", icao: "FDB", name: "flydubai" },
  { iata: "G9", icao: "AXB", name: "Air Arabia" },
  { iata: "GF", icao: "GFA", name: "Gulf Air" },
  { iata: "WY", icao: "OMA", name: "Oman Air" },
  { iata: "ME", icao: "MEA", name: "Middle East Airlines" },
  { iata: "RJ", icao: "RJA", name: "Royal Jordanian" },
  { iata: "TK", icao: "THY", name: "Turkish Airlines" },
  { iata: "PC", icao: "PGT", name: "Pegasus Airlines" },
  { iata: "LY", icao: "ELY", name: "El Al Israel Airlines" },
  // European carriers
  { iata: "AF", icao: "AFR", name: "Air France" },
  { iata: "BA", icao: "BAW", name: "British Airways" },
  { iata: "LH", icao: "DLH", name: "Lufthansa" },
  { iata: "KL", icao: "KLM", name: "KLM Royal Dutch Airlines" },
  { iata: "TP", icao: "TAP", name: "TAP Air Portugal" },
  { iata: "LX", icao: "SWR", name: "Swiss International Air Lines" },
  { iata: "SN", icao: "BEL", name: "Brussels Airlines" },
  { iata: "VS", icao: "VIR", name: "Virgin Atlantic" },
  { iata: "IB", icao: "IBE", name: "Iberia" },
  { iata: "AZ", icao: "ITY", name: "ITA Airways" },
  { iata: "NO", icao: "NOS", name: "Neos" },
  { iata: "NT", icao: "IBB", name: "Binter Canarias" },
  { iata: "SK", icao: "SAS", name: "Scandinavian Airlines" },
  { iata: "LO", icao: "LOT", name: "LOT Polish Airlines" },
  { iata: "OS", icao: "AUA", name: "Austrian Airlines" },
  { iata: "U2", icao: "EZY", name: "easyJet" },
  { iata: "FR", icao: "RYR", name: "Ryanair" },
  { iata: "VY", icao: "VLG", name: "Vueling" },
  { iata: "HV", icao: "TRA", name: "Transavia" },
  { iata: "SS", icao: "CRL", name: "Corsair International" },
  { iata: "BF", icao: "FPO", name: "French Bee" },
  { iata: "TO", icao: "TVF", name: "Transavia France" },
  { iata: "UX", icao: "AEA", name: "Air Europa" },
  { iata: "EW", icao: "EWG", name: "Eurowings" },
  { iata: "DE", icao: "CFG", name: "Condor" },
  { iata: "X3", icao: "TUI", name: "TUI fly" },
  { iata: "TB", icao: "JAF", name: "TUI fly Belgium" },
  { iata: "JU", icao: "JAT", name: "Air Serbia" },
  { iata: "RO", icao: "ROT", name: "TAROM" },
  { iata: "A3", icao: "AEE", name: "Aegean Airlines" },
  { iata: "W6", icao: "WZZ", name: "Wizz Air" },
  { iata: "DY", icao: "NOZ", name: "Norwegian Air Shuttle" },
  { iata: "AY", icao: "FIN", name: "Finnair" },
  { iata: "FI", icao: "ICE", name: "Icelandair" },
  { iata: "LG", icao: "LGL", name: "Luxair" },
  // Americas carriers
  { iata: "AA", icao: "AAL", name: "American Airlines" },
  { iata: "DL", icao: "DAL", name: "Delta Air Lines" },
  { iata: "UA", icao: "UAL", name: "United Airlines" },
  { iata: "CM", icao: "CMA", name: "Copa Airlines" },
  { iata: "LA", icao: "TAM", name: "LATAM Airlines" },
  { iata: "AC", icao: "ACA", name: "Air Canada" },
  { iata: "G3", icao: "GOL", name: "GOL Linhas Aereas" },
  // Asian carriers
  { iata: "CA", icao: "CCA", name: "Air China" },
  { iata: "MU", icao: "CES", name: "China Eastern Airlines" },
  { iata: "CZ", icao: "CSN", name: "China Southern Airlines" },
  { iata: "FM", icao: "CSH", name: "Shanghai Airlines" },
  { iata: "AI", icao: "AIC", name: "Air India" },
  { iata: "SQ", icao: "SIA", name: "Singapore Airlines" },
  { iata: "MH", icao: "MAS", name: "Malaysia Airlines" },
  { iata: "TG", icao: "THA", name: "Thai Airways" },
  { iata: "CX", icao: "CPA", name: "Cathay Pacific" },
  { iata: "KE", icao: "KAL", name: "Korean Air" },
  { iata: "JL", icao: "JAL", name: "Japan Airlines" },
  { iata: "NH", icao: "ANA", name: "All Nippon Airways" },
  { iata: "GA", icao: "GIA", name: "Garuda Indonesia" },
  { iata: "PR", icao: "PAL", name: "Philippine Airlines" },
  { iata: "VN", icao: "VNA", name: "Vietnam Airlines" },
  { iata: "PK", icao: "PIA", name: "Pakistan International Airlines" },
  { iata: "BG", icao: "BGA", name: "Biman Bangladesh Airlines" },
  { iata: "UL", icao: "ALK", name: "SriLankan Airlines" },
  // Cargo carriers
  { iata: "FX", icao: "FDX", name: "FedEx Express" },
  { iata: "5X", icao: "UPS", name: "UPS Airlines" },
  { iata: "5Y", icao: "GTI", name: "Atlas Air" },
  { iata: "CV", icao: "CLX", name: "Cargolux" },
  { iata: "MP", icao: "MPH", name: "Martinair" },
  { iata: "8V", icao: "AST", name: "Astral Aviation" },
  { iata: "", icao: "GEC", name: "Lufthansa Cargo" },
  { iata: "", icao: "RXP", name: "Royal Air Freight" },
  // European regional carriers
  { iata: "NI", icao: "PGA", name: "Portugália Airlines" },
  { iata: "YW", icao: "ANE", name: "Air Nostrum" },
  { iata: "WT", icao: "SWT", name: "Swiftair" },
  // Private/charter
  { iata: "", icao: "VJT", name: "VistaJet" },
];

SEED_AIRLINES.forEach((a) => registry.set(a.iata, a));

/**
 * Merge airlines from flight API responses into the registry.
 * Call this after each /flights fetch with the raw flights array.
 * @param {Array} flights - Raw flight objects from the API
 */
export const mergeAirlinesFromFlights = (flights) => {
  if (!Array.isArray(flights)) return;

  flights.forEach((flight) => {
    const iata = flight.airline?.iata;
    const name = flight.airline?.name;
    if (!iata || !name) return;

    if (!registry.has(iata)) {
      registry.set(iata, {
        iata,
        icao: flight.airline?.icao || "",
        name,
      });
    }
  });
};

/**
 * Get all known airlines as an array.
 * @returns {Array<{iata: string, icao: string, name: string}>}
 */
export const getAirlines = () => Array.from(registry.values());

/**
 * Find an airline by IATA or ICAO code.
 * @param {string} code - IATA or ICAO code
 * @returns {Object|undefined}
 */
export const findAirlineByCode = (code) => {
  if (!code) return undefined;
  const lower = code.toLowerCase();

  for (const airline of registry.values()) {
    if (
      airline.iata.toLowerCase() === lower ||
      airline.icao.toLowerCase() === lower
    ) {
      return airline;
    }
  }
  return undefined;
};

/**
 * Find an airline by partial name match.
 * @param {string} query - Partial airline name
 * @returns {Object|undefined}
 */
export const findAirlineByName = (query) => {
  if (!query) return undefined;
  const lower = query.toLowerCase();

  for (const airline of registry.values()) {
    if (airline.name.toLowerCase().includes(lower)) {
      return airline;
    }
  }
  return undefined;
};
